import { useState } from 'react';
import { BarChart3, BookOpen, ChevronRight, ChevronDown, Edit3, Trash2, PlusCircle, Clock, Sliders, Layers, Target, RotateCcw, Sparkles, Archive } from 'lucide-react';
import { diffAtmVersions, diffFieldLabel } from '../../services/ntTemplateParser';
import { fmtUSD } from '../../utils/formatters';
import { futuresSpecs, getSpec } from '../../config/futuresSpecs';
import { calcTradeStats, calcRStats } from '../../services/statsCalculator';
import { coach, filterMuted, enrichWithMuteAction } from '../../services/coach';
import { getCurrentTradingDay } from '../../services/tradeProcessor';
import { isAtmArchived, getArchivedAt, formatArchiveAge } from '../../utils/atmArchive';
import CoachObservationsPanel from '../Coach/CoachObservationsPanel';
import GlassSelect from '../Common/GlassSelect';

const AtmTab = ({
  darkMode,
  filteredTrades,
  closedFilteredTrades,
  setupAtmBaseTrades,
  ntTemplates,
  ntTemplateVersions,
  ntVersionChanges,
  excludeMistakesFromSetupStats,
  setExcludeMistakesFromSetupStats,
  expandedAtmAnalysis,
  setExpandedAtmAnalysis,
  atmViewMode,
  setAtmViewMode,
  // ATM analytics (from useAtmAnalytics)
  getAllAtmStrategiesWithDetails,
  getAtmStats,
  getAtmBracketAnalysis,
  getTrailParameterCorrelation,
  getVersionPerformance,
  getCrossAtmGroups,
  // Shared setup analytics
  getHourlyStatsForTrades,
  getSetupPerformanceForAtm,
  getRDistributionForTrades,
  // ATM CRUD callbacks
  updateAtmDescription,
  addAtmRule,
  removeAtmRule,
  updateAtmRule,
  updateVersionLabel,
  // v7.6.0 manual archive controls — wired from useAtmManagement.
  // Optional so the component still renders if a host forgets to pass them.
  archiveAtmManually = null,
  unarchiveAtmManually = null,
  // v7.6.0 — exposed so the "Refresh from NT" button in the Analysis
  // toolbar can re-trigger the NT template scan without app restart.
  loadNtTemplates = null,
  // Cross-tab navigation
  onNavigateToSetup,
  // Cross-tab Coach context — passed straight to coach.analyze so atm-scope
  // analyzers (e.g. atm × setup matrix) can reason across the journal.
  allConfluences = [],
  allMistakes = [],
  atmDetails = {},
  setupDetails = {},
  allJournalEntries = {},
  getTradeNetPnL,
  getAccountById,
  onCoachAction,
  mutedObservations = {},
}) => {
  // Local state (view-only, not persisted)
  const [atmSortBy, setAtmSortBy] = useState('totalPnL');
  const [atmMinTrades, setAtmMinTrades] = useState(0);
  const [selectedAtmId, setSelectedAtmId] = useState(null);
  const [editingAtmId, setEditingAtmId] = useState(null);
  const [newAtmRuleText, setNewAtmRuleText] = useState('');
  const [expandedCrossAtmGroup, setExpandedCrossAtmGroup] = useState(null);
  const [comparingVersions, setComparingVersions] = useState(null);
  const [editingVersionLabel, setEditingVersionLabel] = useState(null);
  // Archive section toggle (v7.6.0). Defaults closed so the user's daily
  // view is just the active templates; one click expands the archived
  // list when they want to dig into retired patterns.
  const [showArchivedAtms, setShowArchivedAtms] = useState(false);

  return (
          <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
            {/* Header with View Toggle */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ATM Strategies
                </h2>
                <p className={`text-base transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {atmViewMode === 'library'
                    ? 'Document your ATM strategies with rules and descriptions'
                    : atmViewMode === 'crossAtm'
                    ? 'Compare ATMs sharing the same bracket structure across timeframes'
                    : 'Analyze performance across all ATM strategies'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {/* Clean Metrics Toggle */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExcludeMistakesFromSetupStats(prev => !prev)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      excludeMistakesFromSetupStats
                        ? 'bg-emerald-500'
                        : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                    title={excludeMistakesFromSetupStats ? 'Showing clean metrics (mistakes excluded)' : 'Showing all trades including mistakes'}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      excludeMistakesFromSetupStats ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Clean Metrics
                  </span>
                </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setAtmViewMode('analysis')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    atmViewMode === 'analysis'
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BarChart3 size={20} />
                  Analysis
                </button>
                <button
                  onClick={() => setAtmViewMode('crossAtm')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    atmViewMode === 'crossAtm'
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Layers size={20} />
                  Compare
                </button>
                <button
                  onClick={() => setAtmViewMode('library')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    atmViewMode === 'library'
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BookOpen size={20} />
                  Library
                </button>
              </div>
              </div>
            </div>

            {/* ATM Library View — v7.6.0 splits into active + archived sections.
                Active list renders first (full detail, unchanged behavior).
                Archived list lives under a collapsible header below, default
                closed. Each archived card carries an Archived badge so it's
                visually distinct from active templates. */}
            {atmViewMode === 'library' && (() => {
              const allAtms = getAllAtmStrategiesWithDetails();
              const activeAtms = allAtms.filter(a => !isAtmArchived(ntTemplateVersions, a.name));
              const archivedAtms = allAtms.filter(a => isAtmArchived(ntTemplateVersions, a.name));

              // Single render function for an ATM card so active + archived
              // sections share identical rendering — the only difference is
              // the optional archive badge passed via the `archivedAt` arg.
              const renderAtmCard = (atm, archivedAt) => {
                  const stats = getAtmStats(atm.name);
                  const isExpanded = selectedAtmId === atm.id;
                  const isEditing = editingAtmId === atm.id;

                  return (
                    <div key={atm.id} className={`rounded-lg border transition-colors ${
                      archivedAt
                        // Archived cards: dimmer, dashed border so they read as "for reference"
                        ? (darkMode
                            ? 'bg-black/30 backdrop-blur-md border border-dashed border-amber-400/30 opacity-90'
                            : 'bg-white/10 backdrop-blur-xs border border-dashed border-amber-500/40 opacity-90')
                        : (darkMode
                            ? 'bg-black/45 backdrop-blur-md border border-white/10'
                            : 'bg-white/20 backdrop-blur-xs border border-white/20')
                    }`}>
                      {/* ATM Header - Always visible */}
                      <div
                        className={`p-4 cursor-pointer ${isExpanded ? '' : 'hover:opacity-80'}`}
                        onClick={() => setSelectedAtmId(isExpanded ? null : atm.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Sliders size={20} className={archivedAt ? 'text-amber-500/70' : 'text-purple-500'} />
                            <div>
                              <h3 className={`font-semibold flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {atm.name}
                                {archivedAt && (
                                  // Archive badge — small, amber, with relative timestamp.
                                  // Coach analyzers will skip these for active suggestions
                                  // but still mine them for cross-template learning.
                                  <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                      darkMode
                                        ? 'bg-amber-500/15 text-amber-300 border border-amber-400/30'
                                        : 'bg-amber-100 text-amber-800 border border-amber-300'
                                    }`}
                                    title={`Archived ${formatArchiveAge(archivedAt)} — no longer in NinjaTrader, kept for historical analysis.`}
                                  >
                                    <Archive size={9} />
                                    Archived · {formatArchiveAge(archivedAt)}
                                  </span>
                                )}
                              </h3>
                              {atm.description && !isExpanded && (
                                <p className={`text-base truncate max-w-md ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {atm.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {stats && (
                              <div className="flex items-center gap-3 text-base">
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                  {stats.trades} trades
                                </span>
                                <span className={`font-medium ${parseFloat(stats.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                  {stats.winRate}% WR
                                </span>
                                <span className={`font-semibold ${stats.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {stats.totalPnL >= 0 ? '+' : '-'}${fmtUSD(stats.totalPnL)}
                                </span>
                                {excludeMistakesFromSetupStats && (() => {
                                  const excludedCount = closedFilteredTrades.filter(
                                    t => t.atmStrategy === atm.name && t.mistakes?.length > 0
                                  ).length;
                                  return excludedCount > 0 ? (
                                    <span className="text-xs text-amber-400/70">
                                      ✦ {excludedCount} excluded
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                            {!stats && (
                              <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No trades yet
                              </span>
                            )}
                            {/* v7.6.0 — manual archive / unarchive toggle.
                                Active card → "Archive" button (small, subtle).
                                Archived card → "Unarchive" button.
                                stopPropagation so the click doesn't also
                                expand/collapse the card. */}
                            {!archivedAt && archiveAtmManually && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveAtmManually(atm.name);
                                }}
                                title="Archive this template — mark inactive without removing it from NinjaTrader. Coach will stop suggesting it. You can unarchive any time."
                                className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                  darkMode
                                    ? 'text-gray-400 hover:text-amber-300 hover:bg-amber-500/10 border border-transparent hover:border-amber-400/30'
                                    : 'text-gray-500 hover:text-amber-800 hover:bg-amber-50 border border-transparent hover:border-amber-300'
                                }`}
                              >
                                <Archive size={12} />
                                Archive
                              </button>
                            )}
                            {archivedAt && unarchiveAtmManually && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unarchiveAtmManually(atm.name);
                                }}
                                title="Unarchive this template — mark it actively traded again. Auto-detection resumes from here."
                                className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                  darkMode
                                    ? 'text-amber-300 hover:text-emerald-300 hover:bg-emerald-500/10 border border-amber-400/30 hover:border-emerald-400/30'
                                    : 'text-amber-700 hover:text-emerald-800 hover:bg-emerald-50 border border-amber-300 hover:border-emerald-300'
                                }`}
                              >
                                <RotateCcw size={12} />
                                Unarchive
                              </button>
                            )}
                            <ChevronRight
                              size={20}
                              className={`transition-transform ${isExpanded ? 'rotate-90' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className={`border-t px-4 pb-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {/* Description Section */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <label className={`text-base font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Description
                              </label>
                              {!isEditing && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingAtmId(atm.id); }}
                                  className={`p-1 rounded-sm transition-colors ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                                >
                                  <Edit3 size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                                </button>
                              )}
                            </div>
                            {isEditing ? (
                              <div className="space-y-2">
                                <textarea
                                  value={atm.description}
                                  onChange={(e) => updateAtmDescription(atm.name, e.target.value)}
                                  placeholder="Describe this ATM strategy, when to use it, target conditions..."
                                  rows={3}
                                  className="w-full input-modern text-base"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            ) : (
                              <p className={`text-base ${atm.description ? (darkMode ? 'text-gray-300' : 'text-gray-600') : (darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')}`}>
                                {atm.description || 'No description added. Click edit to add one.'}
                              </p>
                            )}
                          </div>

                          {/* Rules Section */}
                          <div className="mt-4">
                            <label className={`text-base font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Strategy Rules / Parameters
                            </label>
                            <div className="mt-2 space-y-2">
                              {atm.rules.map((rule, idx) => (
                                <div key={idx} className={`flex items-start gap-2 p-2 rounded ${
                                  darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'
                                }`}>
                                  <span className="text-purple-500 mt-0.5">•</span>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={rule}
                                      onChange={(e) => updateAtmRule(atm.name, idx, e.target.value)}
                                      className="flex-1 input-modern text-base"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <span className={`flex-1 text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {rule}
                                    </span>
                                  )}
                                  {isEditing && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); removeAtmRule(atm.name, idx); }}
                                      className="text-red-500 hover:text-red-600 p-1"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {isEditing && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={newAtmRuleText}
                                    onChange={(e) => setNewAtmRuleText(e.target.value)}
                                    placeholder="Add a new rule or parameter..."
                                    className="flex-1 input-modern text-base"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        addAtmRule(atm.name, newAtmRuleText);
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={(e) => { e.stopPropagation(); addAtmRule(atm.name, newAtmRuleText); }}
                                    className="px-3 py-2 bg-purple-500 text-white rounded-sm hover:bg-purple-600 transition-colors"
                                  >
                                    <PlusCircle size={18} />
                                  </button>
                                </div>
                              )}
                              {!isEditing && atm.rules.length === 0 && (
                                <p className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  No rules defined. Click edit to add parameters.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Done Editing Button */}
                          {isEditing && (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingAtmId(null); }}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                              >
                                Done Editing
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
              };
              // ── End of renderAtmCard ──────────────────────────────────

              return (
                <div className="space-y-4">
                  {/* Active templates — full default view */}
                  {activeAtms.map(atm => renderAtmCard(atm, null))}

                  {/* Empty state — only when there are NO templates anywhere
                      (neither active nor archived). Archived-only is still a
                      valid state we want to surface separately below. */}
                  {activeAtms.length === 0 && archivedAtms.length === 0 && (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No ATM strategies found. Import trades with ATM strategy data to see them here.
                    </div>
                  )}

                  {/* Archived templates — collapsible section. Default closed
                      so the daily view is just active templates. Header shows
                      the count so the user can see "I have 12 retired templates"
                      at a glance without expanding. */}
                  {archivedAtms.length > 0 && (
                    <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <button
                        type="button"
                        onClick={() => setShowArchivedAtms(s => !s)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left transition-colors ${
                          darkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-expanded={showArchivedAtms}
                      >
                        <div className="flex items-center gap-2">
                          {showArchivedAtms ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <Archive size={14} className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                          <span className="font-semibold">
                            Archived <span className={`font-mono ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>({archivedAtms.length})</span>
                          </span>
                          <span className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            no longer in NinjaTrader — kept for historical analysis
                          </span>
                        </div>
                      </button>
                      {showArchivedAtms && (
                        <div className="space-y-3 mt-3">
                          {archivedAtms.map(atm => renderAtmCard(atm, getArchivedAt(ntTemplateVersions, atm.name)))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ATM Analysis View */}
            {atmViewMode === 'analysis' && (() => {
              // v7.6.0 — compute archivedCount once for the Analysis view so
              // the toggle footer (rendered as a sibling outside the inner
              // IIFE that runs the ranking) can show the hidden count.
              // Same set the inner IIFE uses to filter — kept in sync via
              // the same isAtmArchived predicate.
              const analysisStrategies = [...new Set(setupAtmBaseTrades.filter(t => t.atmStrategy).map(t => t.atmStrategy))];
              const analysisArchivedCount = analysisStrategies.filter(s => isAtmArchived(ntTemplateVersions, s)).length;
              return (
              <>
                {filteredTrades.filter(t => t.atmStrategy).length === 0 ? (
                  <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No trades with ATM strategies found. Import trades from NinjaTrader with ATM data to see analytics!
                  </div>
                ) : (
                  <>
                    {/* Sort and Filter Dropdowns */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sort by:</span>
                        <GlassSelect
                          value={atmSortBy}
                          onChange={(v) => setAtmSortBy(v)}
                          options={[
                            { value: 'totalPnL', label: 'Total P&L' },
                            { value: 'winRate', label: 'Win Rate' },
                            { value: 'avgPnL', label: 'Avg P&L' },
                            { value: 'profitFactor', label: 'Profit Factor' },
                            { value: 'avgR', label: 'Avg R' },
                            { value: 'trades', label: 'Trade Count' },
                          ]}
                          darkMode={darkMode}
                          wrapperClassName="relative inline-block"
                          className="w-40"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Min trades:</span>
                        <GlassSelect
                          value={atmMinTrades}
                          onChange={(v) => setAtmMinTrades(Number(v))}
                          options={[
                            { value: 0, label: 'All' },
                            { value: 5, label: '5+' },
                            { value: 10, label: '10+' },
                            { value: 20, label: '20+' },
                            { value: 50, label: '50+' },
                          ]}
                          darkMode={darkMode}
                          wrapperClassName="relative inline-block"
                          className="w-24"
                        />
                      </div>
                    </div>

                    {/* v7.6.0 — archive controls moved to the TOP of the
                        Analysis ranking (was a footer below it). At-a-glance
                        view of how many archived templates exist + one-click
                        toggle to surface them in the ranking. Refresh button
                        next to it re-runs the NT template scan so users who
                        just deleted files in NT see the archive transition
                        without having to restart the app. */}
                    {(analysisArchivedCount > 0 || typeof loadNtTemplates === 'function') && (
                      <div className={`mb-4 flex items-center justify-between gap-2 px-3 py-2 rounded-md border ${
                        darkMode ? 'border-white/10 bg-black/20 text-gray-400' : 'border-gray-200 bg-gray-50/50 text-gray-600'
                      }`}>
                        <span className="text-xs italic min-w-0 truncate">
                          {analysisArchivedCount === 0
                            ? 'No archived templates.'
                            : showArchivedAtms
                              ? `Showing ${analysisArchivedCount} archived template${analysisArchivedCount === 1 ? '' : 's'} (dimmed in ranking — kept for retrospective comparison).`
                              : `${analysisArchivedCount} archived template${analysisArchivedCount === 1 ? '' : 's'} hidden from ranking — they’re no longer in NinjaTrader.`}
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {typeof loadNtTemplates === 'function' && (
                            <button
                              type="button"
                              onClick={() => loadNtTemplates()}
                              title="Re-scan the NinjaTrader templates folder. Use this after deleting templates in NT to see them archived without restarting the app."
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded font-medium transition-colors ${
                                darkMode
                                  ? 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10'
                                  : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                              }`}
                            >
                              <RotateCcw size={11} />
                              Refresh from NT
                            </button>
                          )}
                          {analysisArchivedCount > 0 && (
                            <button
                              type="button"
                              onClick={() => setShowArchivedAtms(s => !s)}
                              className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                                darkMode ? 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                              }`}
                            >
                              {showArchivedAtms ? 'Hide archived' : 'Show archived'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid gap-4">
                      {(() => {
                        const atmStrategies = [...new Set(setupAtmBaseTrades.filter(t => t.atmStrategy).map(t => t.atmStrategy))];
                        const atmData = atmStrategies.map(atm => {
                          const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atm);
                          const { wins, losses, totalPnL, avgPnL, winRate, profitFactor } = calcTradeStats(atmTrades);
                          const { avgR } = calcRStats(atmTrades);

                          // Wick Over: only count trades where field is filled in; convert points→ticks for consistency
                          const tradesWithWickOver = atmTrades.filter(t => t.wickOver !== undefined && t.wickOver !== '' && parseFloat(t.wickOver) >= 0 && t.wickOver !== null);
                          const avgWickOver = tradesWithWickOver.length > 0
                            ? tradesWithWickOver.reduce((sum, t) => {
                                let val = parseFloat(t.wickOver);
                                // Convert points to ticks if stored in points
                                if (t.useWickOverTicks === false) {
                                  const spec = getSpec(t.symbol);
                                  if (spec) val = val / spec.tickSize;
                                }
                                return sum + val;
                              }, 0) / tradesWithWickOver.length
                            : null;

                          return {
                            name: atm,
                            trades: atmTrades.length,
                            wins,
                            losses,
                            winRate,
                            totalPnL,
                            avgPnL,
                            profitFactor,
                            avgR,
                            avgWickOver,
                            wickOverCount: tradesWithWickOver.length
                          };
                        });

                        // Filter by minimum trades
                        let filteredAtmData = atmData.filter(atm => atm.trades >= atmMinTrades);

                        // v7.6.0 — hide archived ATMs from the active performance
                        // ranking by default so retired templates don't pollute
                        // the comparison. The footer below this section shows
                        // a count of hidden archived templates with a toggle
                        // to surface them (same `showArchivedAtms` state used
                        // by the Library view, so toggle is consistent across
                        // both views).
                        const archivedCount = filteredAtmData.filter(a => isAtmArchived(ntTemplateVersions, a.name)).length;
                        if (!showArchivedAtms) {
                          filteredAtmData = filteredAtmData.filter(a => !isAtmArchived(ntTemplateVersions, a.name));
                        }

                        // Sort by selected criteria
                        filteredAtmData.sort((a, b) => {
                          switch (atmSortBy) {
                            case 'winRate': return b.winRate - a.winRate;
                            case 'avgPnL': return b.avgPnL - a.avgPnL;
                            case 'profitFactor': 
                              // Handle Infinity
                              if (a.profitFactor === Infinity && b.profitFactor === Infinity) return 0;
                              if (a.profitFactor === Infinity) return -1;
                              if (b.profitFactor === Infinity) return 1;
                              return b.profitFactor - a.profitFactor;
                            case 'avgR': return b.avgR - a.avgR;
                            case 'trades': return b.trades - a.trades;
                            default: return b.totalPnL - a.totalPnL;
                          }
                        });

                        return filteredAtmData.map((atm, index) => {
                          const isExpanded = expandedAtmAnalysis === atm.name;
                          const atmTrades = setupAtmBaseTrades.filter(t => t.atmStrategy === atm.name);
                          const hourlyStats = isExpanded ? getHourlyStatsForTrades(atmTrades) : [];
                          const setupPerformance = isExpanded ? getSetupPerformanceForAtm(atm.name) : [];
                          // Coach observations for this ATM — only computed when
                          // the card is expanded so closed cards don't pay the cost.
                          const atmObservations = isExpanded
                            ? enrichWithMuteAction(
                                filterMuted(
                                  coach.analyze(
                                    { type: 'atm', id: atm.name },
                                    {
                                      allTrades: setupAtmBaseTrades,
                                      anchorDate: getCurrentTradingDay(),
                                      allConfluences,
                                      allMistakes,
                                      atmDetails,
                                      setupDetails,
                                      allJournalEntries,
                                      // Version history powers atmVersionComparison —
                                      // the analyzer attributes each trade to the
                                      // bracket structure active at trade-entry time
                                      // and surfaces WR deltas across versions.
                                      ntTemplateVersions,
                                      // Phase 17 — futuresSpecs enables atmVersionComparison's
                                      // per-bracket attribution to call matchExitsToBrackets()
                                      // with the right tickSize/tickValue per symbol.
                                      futuresSpecs,
                                      getTradeNetPnL,
                                      getAccountById,
                                    }
                                  ),
                                  mutedObservations
                                )
                              )
                            : [];
                          
                          return (
                          <div 
                            key={atm.name}
                            id={`atm-card-${atm.name.replace(/\s+/g, '-')}`}
                            className={`rounded-lg border transition-all ${
                              darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                            } ${isExpanded ? 'ring-2 ring-purple-500' : ''}`}
                          >
                            {/* Clickable Header */}
                            <div
                              className="p-4 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setExpandedAtmAnalysis(isExpanded ? null : atm.name)}
                            >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-bold ${
                                  index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                  index === 1 ? 'bg-gray-400/20 text-gray-400' :
                                  index === 2 ? 'bg-orange-600/20 text-orange-500' :
                                  darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                                }`}>
                                  {index + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {atm.name}
                                    </h3>
                                    <ChevronRight
                                      size={20}
                                      className={`transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isExpanded ? 'rotate-90' : ''}`}
                                    />
                                  </div>
                                  <span className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {atm.trades} trades ({atm.wins}W / {atm.losses}L)
                                    {excludeMistakesFromSetupStats && (() => {
                                      const excludedCount = closedFilteredTrades.filter(
                                        t => t.atmStrategy === atm.name && t.mistakes?.length > 0
                                      ).length;
                                      return excludedCount > 0 ? (
                                        <span className="text-xs text-amber-400/70 ml-2">
                                          ✦ {excludedCount} excluded
                                        </span>
                                      ) : null;
                                    })()}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* v7.6.0 — inline Archive/Unarchive button on
                                    the ranking card so the user can act
                                    directly when they spot a template that
                                    shouldn't be in the active list.
                                    stopPropagation so click doesn't also
                                    expand/collapse the card. */}
                                {!isAtmArchived(ntTemplateVersions, atm.name) && archiveAtmManually && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      archiveAtmManually(atm.name);
                                    }}
                                    title="Archive — mark this template inactive. Removes it from rankings + Coach suggestions. Works even for templates the journal never synced from NT."
                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                      darkMode
                                        ? 'text-gray-400 hover:text-amber-300 hover:bg-amber-500/10 border border-transparent hover:border-amber-400/30'
                                        : 'text-gray-500 hover:text-amber-800 hover:bg-amber-50 border border-transparent hover:border-amber-300'
                                    }`}
                                  >
                                    <Archive size={12} />
                                    Archive
                                  </button>
                                )}
                                {isAtmArchived(ntTemplateVersions, atm.name) && unarchiveAtmManually && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      unarchiveAtmManually(atm.name);
                                    }}
                                    title="Unarchive — return this template to the active rankings."
                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                                      darkMode
                                        ? 'text-amber-300 hover:text-emerald-300 hover:bg-emerald-500/10 border border-amber-400/30 hover:border-emerald-400/30'
                                        : 'text-amber-700 hover:text-emerald-800 hover:bg-emerald-50 border border-amber-300 hover:border-emerald-300'
                                    }`}
                                  >
                                    <RotateCcw size={12} />
                                    Unarchive
                                  </button>
                                )}
                                <div className={`text-2xl font-bold ${atm.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {atm.totalPnL >= 0 ? '+' : '-'}${fmtUSD(atm.totalPnL)}
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                              <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                <div className={`text-xl font-semibold ${atm.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                  {atm.winRate.toFixed(1)}%
                                </div>
                                <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Win Rate</div>
                              </div>
                              <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                <div className={`text-lg font-semibold ${atm.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {atm.avgPnL >= 0 ? '' : '-'}${fmtUSD(atm.avgPnL)}
                                </div>
                                <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg P&L</div>
                              </div>
                              <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                <div className={`text-xl font-semibold ${
                                  atm.profitFactor >= 1.5 ? 'text-green-500' :
                                  atm.profitFactor >= 1 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {atm.profitFactor === Infinity ? '∞' : atm.profitFactor.toFixed(2)}
                                </div>
                                <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Profit Factor</div>
                              </div>
                              <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                <div className={`text-xl font-semibold ${
                                  atm.avgR >= 1 ? 'text-green-500' :
                                  atm.avgR >= 0 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {atm.avgR.toFixed(2)}R
                                </div>
                                <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg R</div>
                              </div>
                              <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                <div className={`text-xl font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {((atm.trades / setupAtmBaseTrades.filter(t => t.atmStrategy).length) * 100).toFixed(1)}%
                                </div>
                                <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>of ATM Trades</div>
                              </div>
                              {atm.avgWickOver !== null && (
                                <div className={`p-2 rounded-sm text-center ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
                                  <div className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {atm.avgWickOver.toFixed(1)}t
                                  </div>
                                  <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Avg Wick Over
                                    <span className={`text-xs ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      ({atm.wickOverCount})
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            </div>

                            {/* Expanded Section - Time of Day & Setup Breakdown */}
                            {isExpanded && (
                              <div className={`border-t px-4 pb-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                {/* AI Coach observations — surfaces atm-scope
                                    bar-context patterns (stop-too-tight, target
                                    leakage, ATM decay). Empty state shown when
                                    no observations fire OR when this ATM has no
                                    trades with `barAnalysis` populated via the
                                    chart load path or Settings → Charts → Run
                                    backfill. */}
                                <div className="mt-4">
                                  <CoachObservationsPanel
                                    observations={atmObservations}
                                    title="AI Coach"
                                    emptyMessage={`Coach needs more analyzed trades for "${atm.name}". Open recent trades' charts (or run Settings → Charts → backfill) to populate bar analysis.`}
                                    darkMode={darkMode}
                                    onAction={onCoachAction}
                                  />
                                </div>

                                {/* Time of Day Performance */}
                                <div className="mt-4">
                                  <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    <Clock size={18} className="text-purple-500" />
                                    Performance by Hour
                                  </h4>
                                  {hourlyStats.length === 0 ? (
                                    <p className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      No time data available for this ATM
                                    </p>
                                  ) : (
                                    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                      {hourlyStats.map(h => {
                                        const maxPnL = Math.max(...hourlyStats.map(x => Math.abs(x.totalPnL)));
                                        const intensity = maxPnL > 0 ? Math.abs(h.totalPnL) / maxPnL : 0;
                                        
                                        return (
                                          <div 
                                            key={h.hour}
                                            className={`p-2 rounded-lg text-center transition-colors`}
                                            style={{
                                              backgroundColor: h.totalPnL > 0 
                                                ? `rgba(34, 197, 94, ${0.15 + intensity * 0.35})` 
                                                : h.totalPnL < 0 
                                                  ? `rgba(239, 68, 68, ${0.15 + intensity * 0.35})`
                                                  : darkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(229, 231, 235, 1)'
                                            }}
                                          >
                                            <div className={`text-base font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                              {h.hour.toString().padStart(2, '0')}:00
                                            </div>
                                            <div className={`text-sm font-bold ${h.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                              {h.totalPnL >= 0 ? '+' : '-'}${Math.abs(h.totalPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                            </div>
                                            <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                              {h.trades} ({h.winRate.toFixed(0)}%)
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Best Setups for this ATM */}
                                <div className="mt-4">
                                  <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    <BarChart3 size={18} className="text-blue-500" />
                                    Setup Performance with {atm.name}
                                  </h4>
                                  {setupPerformance.length === 0 ? (
                                    <p className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      No setup data available for this ATM
                                    </p>
                                  ) : (
                                    <div className="grid gap-2">
                                      {setupPerformance.slice(0, 5).map((setup, idx) => (
                                        <div
                                          key={setup.name}
onClick={() => onNavigateToSetup(setup.name)}
                                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors duration-150 ${
                                            darkMode ? 'bg-gray-700/50 hover:bg-gray-600/60' : 'bg-white hover:bg-gray-50'
                                          }`}
                                          title={`View ${setup.name} in Setup Analytics`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className={`text-base font-bold px-1.5 py-0.5 rounded ${
                                              idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                              darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                                            }`}>
                                              #{idx + 1}
                                            </span>
                                            <span className={`text-base font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                              {setup.name}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-4 text-base">
                                            <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                              {setup.trades} trades
                                            </span>
                                            <span className={setup.winRate >= 50 ? 'text-green-500' : 'text-red-500'}>
                                              {setup.winRate.toFixed(0)}% WR
                                            </span>
                                            <span className={`font-semibold ${setup.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                              {setup.totalPnL >= 0 ? '+' : '-'}${fmtUSD(setup.totalPnL)}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* ──── ATM Structure (from NT XML) ──── */}
                                {(() => {
                                  const bracketAnalysis = getAtmBracketAnalysis(atm.name);
                                  if (!bracketAnalysis.parsedAtm) return null;
                                  const pa = bracketAnalysis.parsedAtm;
                                  return (
                                    <div className="mt-4">
                                      <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        <Sliders size={18} className="text-purple-400" />
                                        ATM Structure
                                        <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {pa.entryQuantity} contracts total
                                        </span>
                                      </h4>
                                      <div className="space-y-2">
                                        {pa.brackets.map((bracket, bi) => (
                                          <div key={bi} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100/80'}`}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`text-base font-bold px-2 py-0.5 rounded ${
                                                bracket.exitType === 'takeProfit'
                                                  ? 'bg-blue-500/20 text-blue-400'
                                                  : 'bg-amber-500/20 text-amber-400'
                                              }`}>
                                                {bracket.exitType === 'takeProfit' ? 'TP' : 'Trail'}
                                              </span>
                                              <span className={`text-base font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                Bracket {bi + 1}: {bracket.quantity}C
                                              </span>
                                              <span className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                | {bracket.stopLossTicks}t SL
                                              </span>
                                              {bracket.exitType === 'takeProfit' && (
                                                <span className="text-base text-blue-400">
                                                  | Target {bracket.targetTicks}t ({(bracket.targetTicks / bracket.stopLossTicks).toFixed(1)}R)
                                                </span>
                                              )}
                                              {bracket.stopStrategy && (
                                                <span className="text-base text-amber-400">
                                                  | {bracket.stopStrategy.name}
                                                </span>
                                              )}
                                              {/* Per-bracket performance */}
                                              {bracketAnalysis.bracketResults[bi]?.trades > 0 && (
                                                <span className={`text-base font-semibold ml-auto ${bracketAnalysis.bracketResults[bi].avgR >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                  Avg {bracketAnalysis.bracketResults[bi].avgR >= 0 ? '+' : ''}{bracketAnalysis.bracketResults[bi].avgR.toFixed(2)}R ({bracketAnalysis.bracketResults[bi].trades} exits)
                                                </span>
                                              )}
                                            </div>
                                            {/* Trail stages */}
                                            {bracket.stopStrategy?.autoTrailSteps?.length > 0 && (
                                              <div className={`mt-2 ml-4 space-y-1 text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {bracket.stopStrategy.autoTrailSteps.map((step, si) => (
                                                  <div key={si} className="flex items-center gap-1">
                                                    <span className="text-amber-500/60">{si === bracket.stopStrategy.autoTrailSteps.length - 1 ? '└─' : '├─'}</span>
                                                    <span>Stage {si + 1}: Stop Loss {step.stopLoss}t, Profit Trigger {step.profitTrigger}t, Frequency {step.frequency}t</span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* ──── R-Distribution per ATM ──── */}
                                {(() => {
                                  const rDist = getRDistributionForTrades(atmTrades);
                                  if (rDist.withR < 3) return null;
                                  return (
                                    <div className="mt-4">
                                      <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        <BarChart3 size={18} className="text-cyan-400" />
                                        R-Distribution
                                        <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {rDist.withR} trades with R data
                                        </span>
                                      </h4>
                                      <div className="space-y-2">
                                        {rDist.data.map((d, i) => (
                                          <div key={d.threshold} className="flex items-center gap-3">
                                            <span className={`text-base w-8 text-right font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{d.threshold}</span>
                                            <div className={`flex-1 h-5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                              <div
                                                className={`h-full rounded-full transition-all duration-500 ${
                                                  i === 0 ? 'bg-green-400' : i === 1 ? 'bg-green-500' : i === 2 ? 'bg-emerald-500' : 'bg-emerald-600'
                                                }`}
                                                style={{ width: `${Math.max(d.pct, 2)}%` }}
                                              />
                                            </div>
                                            <span className={`text-base w-20 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                              {d.pct.toFixed(0)}% ({d.count})
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                      <div className={`mt-2 flex gap-4 text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <span>Avg Winner: <span className="text-green-400 font-semibold">+{rDist.avgWinR.toFixed(2)}R</span></span>
                                        <span>Avg Loser: <span className="text-red-400 font-semibold">-{rDist.avgLossR.toFixed(2)}R</span></span>
                                        <span>Edge: <span className={`font-semibold ${rDist.edgeScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>{rDist.edgeScore >= 0 ? '+' : ''}{rDist.edgeScore.toFixed(2)}</span></span>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* ──── Exit Type Performance (TP vs Trail) ──── */}
                                {(() => {
                                  const bracketAnalysis = getAtmBracketAnalysis(atm.name);
                                  const tvt = bracketAnalysis.tpVsTrail;
                                  if (!tvt || (!tvt.takeProfit && !tvt.trailingStop)) return null;
                                  const tp = tvt.takeProfit;
                                  const tr = tvt.trailingStop;
                                  const tpStronger = tp && tr ? tp.avgR > tr.avgR : !!tp;
                                  return (
                                    <div className="mt-4">
                                      <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        <Target size={18} className="text-blue-400" />
                                        Exit Type Performance
                                      </h4>
                                      <div className="grid grid-cols-2 gap-3">
                                        {/* Take Profit Card */}
                                        <div className={`p-3 rounded-lg border-2 transition-colors ${
                                          tpStronger ? 'border-blue-500/50' : darkMode ? 'border-gray-600' : 'border-gray-300'
                                        } ${darkMode ? 'bg-gray-700/30' : 'bg-blue-50/50'}`}>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-base font-bold text-blue-400">Take Profit</span>
                                            {tpStronger && tp && tr && <span className="text-sm bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-sm font-bold">STRONGER</span>}
                                          </div>
                                          {tp ? (
                                            <div className="space-y-1">
                                              <div className={`text-xl font-bold ${tp.avgR >= 0 ? 'text-green-400' : 'text-red-400'}`}>{tp.avgR >= 0 ? '+' : ''}{tp.avgR.toFixed(2)}R</div>
                                              <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tp.trades} exits | {tp.winRate.toFixed(0)}% WR</div>
                                            </div>
                                          ) : (
                                            <div className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No TP exits</div>
                                          )}
                                        </div>
                                        {/* Trailing Stop Card */}
                                        <div className={`p-3 rounded-lg border-2 transition-colors ${
                                          !tpStronger ? 'border-amber-500/50' : darkMode ? 'border-gray-600' : 'border-gray-300'
                                        } ${darkMode ? 'bg-gray-700/30' : 'bg-amber-50/50'}`}>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-base font-bold text-amber-400">Trailing Stop</span>
                                            {!tpStronger && tp && tr && <span className="text-sm bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-sm font-bold">STRONGER</span>}
                                          </div>
                                          {tr ? (
                                            <div className="space-y-1">
                                              <div className={`text-xl font-bold ${tr.avgR >= 0 ? 'text-green-400' : 'text-red-400'}`}>{tr.avgR >= 0 ? '+' : ''}{tr.avgR.toFixed(2)}R</div>
                                              <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tr.trades} exits | {tr.winRate.toFixed(0)}% WR</div>
                                            </div>
                                          ) : (
                                            <div className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No trail exits</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* ──── Trail Parameter Impact ──── */}
                                {(() => {
                                  const params = getTrailParameterCorrelation(atm.name);
                                  if (params.length < 1) return null;
                                  return (
                                    <div className="mt-4">
                                      <h4 className={`text-base font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        <Layers size={18} className="text-violet-400" />
                                        Trail Parameters
                                        <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Impact on captured R
                                        </span>
                                      </h4>
                                      <div className={`rounded-lg overflow-hidden border ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                                              <th className={`px-3 py-2 text-left text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Stage</th>
                                              <th className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Trigger</th>
                                              <th className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Freq</th>
                                              <th className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>SL</th>
                                              <th className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Trades</th>
                                              <th className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Avg R</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {params.map((p, pi) => (
                                              <tr key={p.paramKey} className={pi % 2 === 0 ? (darkMode ? 'bg-gray-800/50' : 'bg-white') : (darkMode ? 'bg-gray-700/30' : 'bg-gray-50')}>
                                                <td className={`px-3 py-2 text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>S{p.stage}</td>
                                                <td className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{p.profitTrigger}t</td>
                                                <td className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{p.frequency}t</td>
                                                <td className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{p.stopLoss}t</td>
                                                <td className={`px-3 py-2 text-right text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{p.trades}</td>
                                                <td className={`px-3 py-2 text-right text-base font-semibold ${p.avgR >= 0 ? 'text-green-400' : 'text-red-400'}`}>{p.avgR >= 0 ? '+' : ''}{p.avgR.toFixed(2)}R</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* ──── Version History ──── */}
                                {(() => {
                                  const versions = ntTemplateVersions[atm.name] || [];
                                  if (versions.length === 0 && ntTemplates.atm.length === 0) return null;
                                  const isVersionExpanded = comparingVersions?.atmName === atm.name;

                                  // Helper to safely format diff pill text (handles object values that crash React)
                                  const formatDiffPill = (c) => {
                                    const prefix = c.bracket !== undefined ? `B${c.bracket + 1} ` : '';
                                    const step = c.step !== undefined ? ` S${c.step + 1}` : '';
                                    const label = diffFieldLabel(c.field);
                                    if (c.field === 'bracketAdded') return `${prefix}Bracket Added`;
                                    if (c.field === 'bracketRemoved') return `${prefix}Bracket Removed`;
                                    if (c.field === 'stopStrategyAdded') return `${prefix}Trail Strategy Added`;
                                    if (c.field === 'stopStrategyRemoved') return `${prefix}Trail Strategy Removed`;
                                    if (c.field === 'trailStepAdded') return `${prefix}Trail Step${step} Added`;
                                    if (c.field === 'trailStepRemoved') return `${prefix}Trail Step${step} Removed`;
                                    const fmt = (v) => v == null ? '—' : typeof v === 'number' ? `${v}t` : String(v);
                                    return `${prefix}${label}${step}: ${fmt(c.old)} → ${fmt(c.new)}`;
                                  };
                                  const isDiffPositive = (c) => c.field.endsWith('Added') || (typeof c.new === 'number' && typeof c.old === 'number' && c.new > c.old);

                                  return (
                                    <div className="mt-4">
                                      <h4
                                        className={`text-base font-semibold mb-3 flex items-center gap-2 cursor-pointer ${darkMode ? 'text-white' : 'text-gray-900'}`}
                                        onClick={() => setComparingVersions(isVersionExpanded ? null : { atmName: atm.name, compareIdx: null })}
                                      >
                                        <RotateCcw size={18} className="text-amber-400" />
                                        Version History
                                        <span className={`text-base font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          {versions.length === 0 ? 'Tracking started — future changes will appear here'
                                            : versions.length === 1 ? '1 version tracked'
                                            : `${versions.length} versions`}
                                        </span>
                                        {ntVersionChanges.includes(atm.name) && (
                                          <span className="text-base px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">Config changed</span>
                                        )}
                                        <ChevronRight size={18} className={`ml-auto transition-transform ${isVersionExpanded ? 'rotate-90' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                      </h4>

                                      {isVersionExpanded && versions.length > 0 && (
                                        <div className={`space-y-2 rounded-lg p-3 ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                                          {versions.map((ver, vIdx) => {
                                            const prevVer = versions[vIdx + 1];
                                            const changes = prevVer ? diffAtmVersions(prevVer, ver) : [];
                                            const isComparing = comparingVersions?.compareIdx === vIdx;
                                            const perf = isComparing ? getVersionPerformance(atm.name, ver, versions) : null;
                                            const prevPerf = isComparing && prevVer ? getVersionPerformance(atm.name, prevVer, versions) : null;
                                            const snap = ver.snapshot;

                                            return (
                                              <div key={ver.versionId}>
                                                {/* Version card */}
                                                <div className={`rounded-lg p-3 border ${vIdx === 0 ? (darkMode ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-300 bg-amber-50') : (darkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-white')}`}>
                                                  <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                      <Clock size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                                                      <span className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                        {new Date(ver.detectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                        {' '}
                                                        {new Date(ver.detectedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                      </span>
                                                      {vIdx === 0 && <span className="text-base px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-400 font-semibold">CURRENT</span>}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                      {/* Summary badge */}
                                                      <span className={`text-base px-2 py-0.5 rounded-sm ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                                        {snap?.entryQuantity || '?'}C | {snap?.brackets?.[0]?.stopLossTicks || '?'}t SL
                                                        {snap?.brackets?.map(b => b.exitType === 'takeProfit' ? ` | ${b.targetTicks}t TP` : ' | Trail').join('')}
                                                      </span>
                                                      {prevVer && (
                                                        <button
                                                          onClick={(e) => { e.stopPropagation(); setComparingVersions({ atmName: atm.name, compareIdx: isComparing ? null : vIdx }); }}
                                                          className={`text-base px-2 py-0.5 rounded-sm font-medium transition-colors ${isComparing ? 'bg-blue-500 text-white' : (darkMode ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-600 hover:bg-blue-200')}`}
                                                        >
                                                          {isComparing ? 'Hide' : 'Compare ▼'}
                                                        </button>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Editable label */}
                                                  <div className="mt-1 flex items-center gap-1">
                                                    {editingVersionLabel === ver.versionId ? (
                                                      <div className="flex items-center gap-1 flex-1">
                                                        <input
                                                          type="text"
                                                          defaultValue={ver.label}
                                                          placeholder="Add a note (e.g., Widened TP for more R)"
                                                          className="flex-1 input-modern text-base"
                                                          autoFocus
                                                          onKeyDown={(e) => {
                                                            if (e.key === 'Enter') updateVersionLabel(atm.name, ver.versionId, e.target.value);
                                                            if (e.key === 'Escape') setEditingVersionLabel(null);
                                                          }}
                                                          onBlur={(e) => updateVersionLabel(atm.name, ver.versionId, e.target.value)}
                                                        />
                                                      </div>
                                                    ) : (
                                                      <span
                                                        className={`text-base cursor-pointer flex items-center gap-1 ${ver.label ? (darkMode ? 'text-gray-300' : 'text-gray-600') : (darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic')}`}
                                                        onClick={() => setEditingVersionLabel(ver.versionId)}
                                                      >
                                                        <Edit3 size={14} />
                                                        {ver.label || 'Click to add note...'}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Diff pills between versions */}
                                                {changes.length > 0 && !isComparing && (
                                                  <div className="flex flex-wrap gap-1 py-1.5 px-2">
                                                    {changes.slice(0, 6).map((c, ci) => (
                                                      <span key={ci} className={`text-base px-2 py-0.5 rounded-full font-medium ${isDiffPositive(c) ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                                                        {formatDiffPill(c)}
                                                      </span>
                                                    ))}
                                                    {changes.length > 6 && <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>+{changes.length - 6} more</span>}
                                                  </div>
                                                )}

                                                {/* ──── Side-by-side comparison panel ──── */}
                                                {isComparing && prevVer && (
                                                  <div className={`mt-2 rounded-lg border p-3 ${darkMode ? 'border-blue-500/30 bg-blue-500/5' : 'border-blue-200 bg-blue-50'}`}>
                                                    {/* Diff pills */}
                                                    {changes.length > 0 && (
                                                      <div className="flex flex-wrap gap-1 mb-3">
                                                        <span className={`text-base font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Changes:</span>
                                                        {changes.map((c, ci) => (
                                                          <span key={ci} className={`text-base px-2 py-0.5 rounded-full font-medium ${isDiffPositive(c) ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                                                            {formatDiffPill(c)}
                                                          </span>
                                                        ))}
                                                      </div>
                                                    )}

                                                    {/* Side-by-side performance */}
                                                    <div className="grid grid-cols-2 gap-3">
                                                      {/* Previous version */}
                                                      <div className={`rounded-lg p-3 border ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-white'}`}>
                                                        <div className={`text-base font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                          PREVIOUS — {new Date(prevVer.detectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                          {prevVer.label && <span className="ml-1 font-normal italic">({prevVer.label})</span>}
                                                        </div>
                                                        {/* Structure summary */}
                                                        <div className="space-y-1 mb-3">
                                                          {prevVer.snapshot?.brackets?.map((b, bi) => (
                                                            <div key={bi} className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                              B{bi + 1}: {b.quantity}C | {b.stopLossTicks}t SL |{' '}
                                                              {b.exitType === 'takeProfit' ? <span className="text-blue-400">{b.targetTicks}t TP</span> : <span className="text-amber-400">Trail{b.stopStrategy ? ` (${b.stopStrategy.name})` : ''}</span>}
                                                              {b.stopStrategy?.autoTrailSteps?.[0] && (
                                                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{' '}SL {b.stopStrategy.autoTrailSteps[0].stopLoss}t | PT {b.stopStrategy.autoTrailSteps[0].profitTrigger}t | Freq {b.stopStrategy.autoTrailSteps[0].frequency}t</span>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                        {/* Performance stats */}
                                                        {prevPerf && prevPerf.trades > 0 ? (
                                                          <div className="space-y-1">
                                                            <div className="flex justify-between text-base"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Trades</span><span className={darkMode ? 'text-white' : 'text-gray-800'}>{prevPerf.trades}</span></div>
                                                            <div className="flex justify-between text-base"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Win Rate</span><span className={darkMode ? 'text-white' : 'text-gray-800'}>{prevPerf.winRate.toFixed(1)}%</span></div>
                                                            <div className="flex justify-between text-base"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Avg R</span><span className={prevPerf.avgR !== null && prevPerf.avgR >= 0 ? 'text-green-400' : 'text-red-400'}>{prevPerf.avgR !== null ? `${prevPerf.avgR >= 0 ? '+' : ''}${prevPerf.avgR.toFixed(2)}R` : '—'}</span></div>
                                                            <div className="flex justify-between text-base"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Edge</span><span className={prevPerf.edgeScore >= 0 ? 'text-green-400' : 'text-red-400'}>{prevPerf.edgeScore >= 0 ? '+' : ''}{prevPerf.edgeScore.toFixed(2)}</span></div>
                                                            <div className="flex justify-between text-sm"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>P&L</span><span className={prevPerf.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}>${Math.abs(prevPerf.totalPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span></div>
                                                          </div>
                                                        ) : (
                                                          <div className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No trades under this version</div>
                                                        )}
                                                      </div>

                                                      {/* Current version */}
                                                      <div className={`rounded-lg p-3 border ${darkMode ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-200 bg-amber-50'}`}>
                                                        <div className={`text-base font-semibold mb-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                          {vIdx === 0 ? 'CURRENT' : 'NEWER'} — {new Date(ver.detectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                          {ver.label && <span className="ml-1 font-normal italic">({ver.label})</span>}
                                                        </div>
                                                        {/* Structure summary with change highlights */}
                                                        <div className="space-y-1 mb-3">
                                                          {snap?.brackets?.map((b, bi) => {
                                                            const bracketChanged = changes.some(c => c.bracket === bi);
                                                            return (
                                                              <div key={bi} className={`text-base ${bracketChanged ? 'font-semibold' : ''} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                                B{bi + 1}: {b.quantity}C | {b.stopLossTicks}t SL |{' '}
                                                                {b.exitType === 'takeProfit' ? <span className={bracketChanged ? 'text-green-400' : 'text-blue-400'}>{b.targetTicks}t TP</span> : <span className={bracketChanged ? 'text-green-400' : 'text-amber-400'}>Trail{b.stopStrategy ? ` (${b.stopStrategy.name})` : ''}</span>}
                                                                {b.stopStrategy?.autoTrailSteps?.[0] && (
                                                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{' '}SL {b.stopStrategy.autoTrailSteps[0].stopLoss}t | PT {b.stopStrategy.autoTrailSteps[0].profitTrigger}t | Freq {b.stopStrategy.autoTrailSteps[0].frequency}t</span>
                                                                )}
                                                                {bracketChanged && <span className="ml-1 text-green-400">●</span>}
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                        {/* Performance stats with comparison badges */}
                                                        {perf && perf.trades > 0 ? (
                                                          <div className="space-y-1">
                                                            <div className="flex justify-between text-base"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Trades</span><span className={darkMode ? 'text-white' : 'text-gray-800'}>{perf.trades}</span></div>
                                                            <div className="flex justify-between text-base">
                                                              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Win Rate</span>
                                                              <span className={darkMode ? 'text-white' : 'text-gray-800'}>
                                                                {perf.winRate.toFixed(1)}%
                                                                {prevPerf && prevPerf.trades > 0 && (
                                                                  <span className={`ml-1 text-sm ${perf.winRate > prevPerf.winRate ? 'text-green-400' : perf.winRate < prevPerf.winRate ? 'text-red-400' : 'text-gray-500'}`}>
                                                                    {perf.winRate > prevPerf.winRate ? '▲' : perf.winRate < prevPerf.winRate ? '▼' : '—'}
                                                                  </span>
                                                                )}
                                                              </span>
                                                            </div>
                                                            <div className="flex justify-between text-base">
                                                              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Avg R</span>
                                                              <span className={perf.avgR !== null && perf.avgR >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                                {perf.avgR !== null ? `${perf.avgR >= 0 ? '+' : ''}${perf.avgR.toFixed(2)}R` : '—'}
                                                                {prevPerf && prevPerf.trades > 0 && perf.avgR !== null && prevPerf.avgR !== null && (
                                                                  <span className={`ml-1 text-sm ${perf.avgR > prevPerf.avgR ? 'text-green-400' : perf.avgR < prevPerf.avgR ? 'text-red-400' : 'text-gray-500'}`}>
                                                                    {perf.avgR > prevPerf.avgR ? '▲' : perf.avgR < prevPerf.avgR ? '▼' : '—'}
                                                                  </span>
                                                                )}
                                                              </span>
                                                            </div>
                                                            <div className="flex justify-between text-base">
                                                              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Edge</span>
                                                              <span className={perf.edgeScore >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                                {perf.edgeScore >= 0 ? '+' : ''}{perf.edgeScore.toFixed(2)}
                                                                {prevPerf && prevPerf.trades > 0 && (
                                                                  <span className={`ml-1 text-xs ${perf.edgeScore > prevPerf.edgeScore ? 'text-green-400' : perf.edgeScore < prevPerf.edgeScore ? 'text-red-400' : 'text-gray-500'}`}>
                                                                    {perf.edgeScore > prevPerf.edgeScore ? '▲' : perf.edgeScore < prevPerf.edgeScore ? '▼' : '—'}
                                                                  </span>
                                                                )}
                                                              </span>
                                                            </div>
                                                            <div className="flex justify-between text-sm"><span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>P&L</span><span className={perf.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}>${Math.abs(perf.totalPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span></div>
                                                            {/* Overall verdict */}
                                                            {prevPerf && prevPerf.trades >= 3 && perf.trades >= 3 && perf.avgR !== null && prevPerf.avgR !== null && (
                                                              <div className={`mt-2 text-center text-base font-bold px-2 py-1 rounded-sm ${perf.avgR > prevPerf.avgR ? 'bg-green-500/20 text-green-400' : perf.avgR < prevPerf.avgR ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                                {perf.avgR > prevPerf.avgR ? 'IMPROVED' : perf.avgR < prevPerf.avgR ? 'DECLINED' : 'NEUTRAL'}
                                                                {' '}({perf.avgR > prevPerf.avgR ? '+' : ''}{(perf.avgR - prevPerf.avgR).toFixed(2)}R)
                                                              </div>
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <div className={`text-base italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No trades under this version yet</div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                        });
                      })()}
                    </div>
                  </>
                )}
              </>
              );
            })()}

            {/* ═══ Cross-ATM Comparison View ═══ */}
            {atmViewMode === 'crossAtm' && (
              <>
                {(() => {
                  const crossGroups = getCrossAtmGroups();

                  if (crossGroups.length === 0) {
                    return (
                      <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <Layers size={48} className="mx-auto mb-4 opacity-30" />
                        <p className="text-xl font-medium mb-2">No matching ATM groups found</p>
                        <p className="text-base max-w-md mx-auto">
                          Cross-ATM comparison groups ATMs that share the same bracket structure (same contracts, SL, exit types).
                          Use the same ATM template across different timeframes to see performance differences here.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-6">
                      <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Found {crossGroups.length} group{crossGroups.length !== 1 ? 's' : ''} of ATMs sharing the same bracket structure
                      </div>

                      {crossGroups.map((group, _gIdx) => {
                        const isGroupExpanded = expandedCrossAtmGroup === group.fingerprint;
                        const refTemplate = group.template;
                        const totalTrades = group.members.reduce((s, m) => s + m.trades, 0);
                        const bestMember = group.members.reduce((best, m) => (!best || m.avgR > best.avgR) ? m : best, null);
                        const worstMember = group.members.reduce((worst, m) => (!worst || m.avgR < worst.avgR) ? m : worst, null);

                        return (
                          <div key={group.fingerprint} className={`rounded-xl border transition-all ${
                            isGroupExpanded
                              ? darkMode ? 'border-purple-500/50 bg-purple-500/5' : 'border-purple-300 bg-purple-50/50'
                              : darkMode ? 'border-white/10 bg-black/30' : 'border-gray-200 bg-white/40'
                          }`}>
                            {/* Group Header */}
                            <div
                              className="p-4 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setExpandedCrossAtmGroup(isGroupExpanded ? null : group.fingerprint)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                                    <Layers size={20} className="text-purple-500" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {group.members.length} ATMs
                                      </span>
                                      {group.members.some(m => m.timeframeHint) && (
                                        <div className="flex gap-1">
                                          {group.members.filter(m => m.timeframeHint).map(m => (
                                            <span key={m.name} className={`text-sm px-1.5 py-0.5 rounded font-medium ${
                                              darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              {m.timeframeHint}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {refTemplate ? (
                                        <span>{refTemplate.entryQuantity}C | {refTemplate.brackets.map(b =>
                                          `${b.quantity}C ${b.exitType === 'takeProfit' ? b.targetTicks + 't TP' : 'Trail'}`
                                        ).join(' + ')}</span>
                                      ) : (
                                        <span>Same structure detected from trade data</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {totalTrades} trades
                                  </span>
                                  {bestMember && (
                                    <span className={`text-base px-2 py-1 rounded font-medium ${
                                      darkMode ? 'bg-green-500/15 text-green-400' : 'bg-green-100 text-green-700'
                                    }`}>
                                      Best: {bestMember.timeframeHint || bestMember.name.substring(0, 20)}
                                    </span>
                                  )}
                                  <ChevronRight
                                    size={20}
                                    className={`transition-transform ${isGroupExpanded ? 'rotate-90' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Expanded Comparison */}
                            {isGroupExpanded && (
                              <div className={`border-t px-4 pb-4 ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>

                                {/* ── Shared Bracket Structure ── */}
                                {refTemplate && (
                                  <div className="mt-4 mb-5">
                                    <h4 className={`text-base font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      Shared Structure
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {refTemplate.brackets.map((b, bi) => (
                                        <div key={bi} className={`px-3 py-1.5 rounded-lg text-base font-medium ${
                                          darkMode ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700 border border-gray-200'
                                        }`}>
                                          B{bi + 1}: {b.quantity}C | {b.stopLossTicks}t SL |{' '}
                                          {b.exitType === 'takeProfit'
                                            ? <span className="text-blue-400">{b.targetTicks}t TP</span>
                                            : <span className="text-amber-400">Trail{b.stopStrategy ? ` (${(b.stopStrategy.autoTrailSteps || []).length} steps)` : ''}</span>
                                          }
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* ── Side-by-Side Performance Cards ── */}
                                <h4 className={`text-base font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Performance Comparison
                                </h4>
                                <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${Math.min(group.members.length, 4)}, 1fr)` }}>
                                  {group.members.map((member, _mIdx) => {
                                    const isBest = bestMember && member.name === bestMember.name;
                                    const isWorst = worstMember && member.name === worstMember.name && group.members.length > 2;

                                    return (
                                      <div key={member.name} className={`rounded-lg p-3 border transition-all ${
                                        isBest
                                          ? darkMode ? 'border-green-500/40 bg-green-500/5' : 'border-green-300 bg-green-50'
                                          : isWorst
                                          ? darkMode ? 'border-red-500/30 bg-red-500/5' : 'border-red-200 bg-red-50/50'
                                          : darkMode ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white/60'
                                      }`}>
                                        {/* Card header */}
                                        <div className="flex items-center justify-between mb-3">
                                          <div>
                                            {member.timeframeHint && (
                                              <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {member.timeframeHint}
                                              </div>
                                            )}
                                            <div className={`text-sm truncate max-w-[200px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} title={member.name}>
                                              {member.name}
                                            </div>
                                          </div>
                                          {isBest && (
                                            <span className="text-xs px-1.5 py-0.5 rounded-sm bg-green-500/20 text-green-400 font-bold">BEST</span>
                                          )}
                                          {isWorst && (
                                            <span className="text-xs px-1.5 py-0.5 rounded-sm bg-red-500/20 text-red-400 font-bold">WEAKEST</span>
                                          )}
                                        </div>

                                        {/* Stats grid */}
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-center">
                                            <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Trades</span>
                                            <span className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{member.trades}</span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Win Rate</span>
                                            <span className={`text-base font-semibold ${member.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                              {member.winRate.toFixed(1)}%
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Avg R</span>
                                            <span className={`text-base font-bold ${member.avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                              {member.avgR >= 0 ? '+' : ''}{member.avgR.toFixed(2)}R
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Avg P&L</span>
                                            <span className={`text-sm font-medium ${member.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                              {member.avgPnL >= 0 ? '+' : '-'}${fmtUSD(member.avgPnL)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className={`text-base ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>PF</span>
                                            <span className={`text-base font-medium ${member.profitFactor >= 1 ? 'text-green-500' : 'text-red-500'}`}>
                                              {member.profitFactor === Infinity ? '∞' : member.profitFactor.toFixed(2)}
                                            </span>
                                          </div>
                                          <div className={`pt-2 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                                            <div className="flex justify-between items-center">
                                              <span className={`text-base font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total P&L</span>
                                              <span className={`text-sm font-bold ${member.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                {member.totalPnL >= 0 ? '+' : '-'}${fmtUSD(member.totalPnL)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Per-bracket R capture (if bracket analysis available) */}
                                        {member.bracketAnalysis?.bracketResults?.length > 0 && (
                                          <div className={`mt-3 pt-2 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                                            <div className={`text-sm font-semibold mb-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                              Bracket Avg R
                                            </div>
                                            <div className="space-y-1">
                                              {member.bracketAnalysis.bracketResults.map(br => (
                                                <div key={br.bracketIndex} className="flex justify-between items-center">
                                                  <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    B{br.bracketIndex + 1}
                                                    <span className={`ml-1 ${br.exitType === 'takeProfit' ? 'text-blue-400' : 'text-amber-400'}`}>
                                                      {br.exitType === 'takeProfit' ? 'TP' : 'Trail'}
                                                    </span>
                                                  </span>
                                                  <span className={`text-sm font-semibold ${br.avgR >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {br.avgR >= 0 ? '+' : ''}{br.avgR.toFixed(2)}R
                                                    <span className={`ml-1 font-normal ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                                      ({br.trades}t)
                                                    </span>
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* TP vs Trail mini comparison */}
                                        {member.bracketAnalysis?.tpVsTrail && (
                                          <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                                            <div className="flex gap-2">
                                              {member.bracketAnalysis.tpVsTrail.takeProfit && (
                                                <div className={`flex-1 text-center py-1 rounded-sm text-sm ${darkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                                                  <div className="text-blue-400 font-semibold">TP</div>
                                                  <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                                    {member.bracketAnalysis.tpVsTrail.takeProfit.avgR.toFixed(2)}R
                                                  </div>
                                                </div>
                                              )}
                                              {member.bracketAnalysis.tpVsTrail.trailingStop && (
                                                <div className={`flex-1 text-center py-1 rounded-sm text-sm ${darkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                                                  <div className="text-amber-400 font-semibold">Trail</div>
                                                  <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                                    {member.bracketAnalysis.tpVsTrail.trailingStop.avgR.toFixed(2)}R
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* ── Comparative Insights ── */}
                                {group.members.length >= 2 && bestMember && worstMember && bestMember.name !== worstMember.name && (
                                  <div className={`mt-4 rounded-lg p-3 ${darkMode ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
                                    <div className={`text-sm font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                      <Sparkles size={16} />
                                      Insights
                                    </div>
                                    <div className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      <div>
                                        <span className="font-medium text-green-400">{bestMember.timeframeHint || bestMember.name.substring(0, 25)}</span>
                                        {' '}leads with{' '}
                                        <span className="font-semibold">{bestMember.avgR >= 0 ? '+' : ''}{bestMember.avgR.toFixed(2)}R</span>
                                        {' '}avg across {bestMember.trades} trades
                                        {bestMember.winRate !== worstMember.winRate && (
                                          <span> ({bestMember.winRate.toFixed(0)}% WR vs {worstMember.winRate.toFixed(0)}%)</span>
                                        )}
                                      </div>
                                      {(() => {
                                        const rSpread = bestMember.avgR - worstMember.avgR;
                                        if (rSpread > 0.3) {
                                          return (
                                            <div>
                                              R-spread between best and weakest: <span className="font-semibold text-amber-400">{rSpread.toFixed(2)}R</span>
                                              {rSpread > 1 ? ' — significant edge difference, consider focusing on the stronger timeframe' : ''}
                                            </div>
                                          );
                                        }
                                        return (
                                          <div>
                                            Performance is <span className="font-semibold">fairly consistent</span> across timeframes (spread: {rSpread.toFixed(2)}R)
                                          </div>
                                        );
                                      })()}
                                      {(() => {
                                        // Per-bracket comparison insight
                                        const membersWithBrackets = group.members.filter(m => m.bracketAnalysis?.bracketResults?.length > 0);
                                        if (membersWithBrackets.length < 2) return null;
                                        const trailBrackets = membersWithBrackets.map(m => {
                                          const trails = m.bracketAnalysis.bracketResults.filter(b => b.exitType === 'trailingStop');
                                          if (trails.length === 0) return null;
                                          const avgTrailR = trails.reduce((s, b) => s + b.avgR, 0) / trails.length;
                                          return { name: m.timeframeHint || m.name.substring(0, 20), avgTrailR, trades: trails.reduce((s, b) => s + b.trades, 0) };
                                        }).filter(Boolean);
                                        if (trailBrackets.length < 2) return null;
                                        const bestTrail = trailBrackets.reduce((b, m) => m.avgTrailR > b.avgTrailR ? m : b);
                                        const worstTrail = trailBrackets.reduce((w, m) => m.avgTrailR < w.avgTrailR ? m : w);
                                        if (bestTrail.name === worstTrail.name) return null;
                                        return (
                                          <div>
                                            Trail stops work best on <span className="font-medium text-amber-400">{bestTrail.name}</span>
                                            {' '}({bestTrail.avgTrailR >= 0 ? '+' : ''}{bestTrail.avgTrailR.toFixed(2)}R avg)
                                            vs <span className="font-medium">{worstTrail.name}</span>
                                            {' '}({worstTrail.avgTrailR >= 0 ? '+' : ''}{worstTrail.avgTrailR.toFixed(2)}R)
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
  );
};

export default AtmTab;
